import { useEffect } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Module #28 chunk 3 — Capacitor deep-link auth handler.
 *
 * Listens for `appUrlOpen` from `@capacitor/app` and consumes any
 * `auctisync://auth/callback?...#...` URLs by handing the embedded tokens
 * to Supabase. Without this, the Capacitor app can register the URL
 * scheme + receive the OS handoff, but Supabase will never see the access
 * token / refresh token because they live in the URL hash (which only
 * `detectSessionInUrl: true` consumes on page load — irrelevant when the
 * app is already running).
 *
 * URL shapes we handle (mirrors the web AuthCallback flow):
 *
 *   Magic-link / email confirmation / invite:
 *     auctisync://auth/callback#access_token=...&refresh_token=...&type=magiclink
 *     auctisync://auth/callback?next=/admin/...#access_token=...&type=...
 *
 *   PKCE OAuth (future — Google / Apple sign-in):
 *     auctisync://auth/callback?code=...
 *
 *   Password recovery — routes through SAME callback; `type=recovery` in
 *   hash triggers the reset-password flow in `AuthCallback.tsx`.
 *
 * After setting the session we ALSO navigate to /auth/callback so the
 * existing AuthCallback component handles `next=` routing + recovery
 * detection + error display — single source of truth for post-auth state.
 *
 * No-op outside Capacitor: dynamic import of `@capacitor/app` fails
 * cleanly when running in a regular browser (the web bundle has no
 * native shim), so this hook costs ~zero on web.
 */

interface CapacitorAppShape {
  addListener: (
    eventName: 'appUrlOpen',
    listenerFunc: (event: { url: string }) => void,
  ) => Promise<{ remove: () => Promise<void> }>;
}

interface CapacitorCoreShape {
  isNativePlatform: () => boolean;
}

async function loadCapacitorAppModule(): Promise<{
  Capacitor: CapacitorCoreShape;
  App: CapacitorAppShape;
} | null> {
  try {
    const core = (await import('@capacitor/core')) as { Capacitor?: CapacitorCoreShape };
    const app = (await import('@capacitor/app')) as { App?: CapacitorAppShape };
    if (!core.Capacitor || !app.App) return null;
    return { Capacitor: core.Capacitor, App: app.App };
  } catch {
    return null;
  }
}

function isAuthCallbackUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.pathname === '/auth/callback' || parsed.pathname === '//auth/callback';
  } catch {
    return false;
  }
}

async function consumeAuthUrl(url: string): Promise<void> {
  const parsed = new URL(url);
  const hashParams = new URLSearchParams(parsed.hash.startsWith('#') ? parsed.hash.slice(1) : '');
  const accessToken = hashParams.get('access_token');
  const refreshToken = hashParams.get('refresh_token');
  const code = parsed.searchParams.get('code');

  // Magic link / recovery / invite — tokens come in the URL hash.
  if (accessToken && refreshToken) {
    await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
  } else if (code) {
    // OAuth PKCE flow — exchange the authorization code for a session.
    await supabase.auth.exchangeCodeForSession(code);
  }

  // Always route through the React AuthCallback so `next=` + recovery
  // + error states are handled in ONE place.
  const next = parsed.searchParams.get('next');
  const errorDescription = hashParams.get('error_description');
  const params = new URLSearchParams();
  if (next) params.set('next', next);

  let target = '/auth/callback';
  if (params.toString().length > 0) target += `?${params.toString()}`;
  // Preserve error hash so AuthCallback can surface it.
  if (errorDescription) target += `#error_description=${encodeURIComponent(errorDescription)}`;

  // Capacitor apps use the same React Router instance — pushState the
  // target so the AuthCallback route mounts + finishes routing.
  window.history.replaceState({}, '', target);
  // Fire a popstate so any subscribed router (react-router-dom v7) picks
  // up the new path. Pure pushState alone doesn't trigger react-router.
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useCapacitorDeepLinkAuth(): void {
  useEffect(() => {
    let cancelled = false;
    let removeListener: (() => Promise<void>) | null = null;

    void (async () => {
      const mods = await loadCapacitorAppModule();
      if (!mods || cancelled) return;
      if (!mods.Capacitor.isNativePlatform()) return;

      const listener = await mods.App.addListener('appUrlOpen', (event) => {
        if (!event?.url) return;
        if (!isAuthCallbackUrl(event.url)) return;
        // Fire-and-forget; errors swallowed because Supabase setSession
        // already surfaces errors via onAuthStateChange.
        void consumeAuthUrl(event.url).catch(() => {
          // ignore — AuthCallback's session-polling will display the
          // generic "link did not authenticate" message on its own.
        });
      });

      if (cancelled) {
        void listener.remove();
        return;
      }
      removeListener = () => listener.remove();
    })();

    return () => {
      cancelled = true;
      if (removeListener) void removeListener();
    };
  }, []);
}
